import { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MenuItem from '@/components/MenuItem';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO, { ImageMetadata } from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import chipsImage from '@/assets/chips-bowl.png';
import cheeseDipImg from '@/assets/menu/cheese-dip.jpg';
import guacamoleImg from '@/assets/menu/guacamole.jpg';
import wingsImg from '@/assets/menu/wings.jpg';
import nachosImg from '@/assets/menu/nachos.jpg';
import tacoSaladImg from '@/assets/menu/taco-salad.jpg';
import enchiladasImg from '@/assets/menu/enchiladas.jpg';
import quesadillaImg from '@/assets/menu/quesadilla.jpg';
import burritoImg from '@/assets/menu/burrito.jpg';
import grilledChickenImg from '@/assets/menu/grilled-chicken.jpg';
import steakImg from '@/assets/menu/steak.jpg';
import fajitasImg from '@/assets/menu/fajitas.jpg';
import tacosImg from '@/assets/menu/tacos.jpg';
import shrimpImg from '@/assets/menu/shrimp.jpg';
import kidsMealImg from '@/assets/menu/kids-meal.jpg';
import riceBeansImg from '@/assets/menu/rice-beans.jpg';
import soupImg from '@/assets/menu/soup.jpg';
import churrosImg from '@/assets/menu/churros.jpg';
import drinksImg from '@/assets/menu/drinks.jpg';
import flanImg from '@/assets/menu/flan.jpg';
import salmonImg from '@/assets/menu/salmon.jpg';

interface MenuItemData {
  id: string;
  name: string;
  price: string;
  description: string | null;
  category: string;
  image_url: string | null;
}

interface MenuCategory {
  id: string;
  name: string;
  display_name: string;
  display_order: number;
}

const Menu = () => {
  const { t } = useLanguage();
  const [menuItems, setMenuItems] = useState<Record<string, MenuItemData[]>>({});
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const { contactInfo } = useSiteSettings();

  useEffect(() => {
    fetchCategories();
    fetchMenuItems();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      container?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [menuItems]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      // Group items by category
      const grouped = (data || []).reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      }, {} as Record<string, MenuItemData[]>);

      setMenuItems(grouped);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('loadingMenu')}</p>
        </div>
      </main>
    );
  }

  // Create display categories - group database categories for display
  const displayCategories = [
    { value: 'appetizers', label: t('appetizers'), includes: ['appetizers', 'nachos'] },
    { value: 'salads', label: t('salads'), includes: ['salads', 'enchiladas'] },
    { value: 'quesadillas', label: t('quesadillas'), includes: ['quesadillas', 'burritos'] },
    { value: 'chicken-specialties', label: t('chickenSpecialties'), includes: ['chicken-specialties', 'steaks', 'fajitas', 'seafood', 'popular-dishes', 'soups'] },
    { value: 'taqueria', label: t('taqueria'), includes: ['taqueria'] },
    { value: 'kids-menu', label: t('kidsAndSidesMenu'), includes: ['kids-menu', 'sides'] },
    { value: 'lunch-menu', label: t('lunchMenu'), includes: ['lunch-menu'] },
    { value: 'desserts', label: t('desserts'), includes: ['desserts'] },
    { value: 'drinks', label: t('drinks'), includes: ['drinks'] },
  ];

  // Add any new categories that aren't in the predefined groups
  const usedCategories = new Set(displayCategories.flatMap(cat => cat.includes));
  categories.forEach(cat => {
    if (!usedCategories.has(cat.name)) {
      displayCategories.push({
        value: cat.name,
        label: cat.display_name,
        includes: [cat.name]
      });
    }
  });

  const imageMap: Record<string, string> = {
    'cheese-dip': cheeseDipImg,
    'guacamole-dip': guacamoleImg,
    'junniors-dip': cheeseDipImg,
    'bean-dip': cheeseDipImg,
    'wings': wingsImg,
    'anafre': nachosImg,
    'cheese-nachos': nachosImg,
    'bean-nachos': nachosImg,
    'beef-chicken-nachos': nachosImg,
    'supreme-nachos': nachosImg,
    'fajita-nachos-chicken': nachosImg,
    'fajita-nachos-steak': nachosImg,
    'fajita-nachos-shrimp': nachosImg,
    'junniors-nachos': nachosImg,
    'taco-salad': tacoSaladImg,
    'grilled-chicken-salad': tacoSaladImg,
    'caesar-salad-chicken': tacoSaladImg,
    'caesar-salad-steak': tacoSaladImg,
    'guacamole-salad': guacamoleImg,
    'supreme-enchiladas': enchiladasImg,
    'enchiladas-de-la-casa': enchiladasImg,
    'enchiladas-al-ranchero': enchiladasImg,
    'quesadilla-cheese': quesadillaImg,
    'quesadilla-chicken': quesadillaImg,
    'quesadilla-steak': quesadillaImg,
    'quesadilla-shrimp': quesadillaImg,
    'junior-quesadilla': quesadillaImg,
    'pork-quesadilla': quesadillaImg,
    'junniors-burrito': burritoImg,
    'caly-burrito': burritoImg,
    'burrito-verde': burritoImg,
    'grilled-chicken-plate': grilledChickenImg,
    'chicken-chimichurri': grilledChickenImg,
    'chipotle-chicken': grilledChickenImg,
    'ribeye': steakImg,
    'sirloin': steakImg,
    't-bone': steakImg,
    'tacos-plate-chicken': tacosImg,
    'tacos-plate-beef': tacosImg,
    'tacos-plate-pastor-carnitas-chicken-chorizo': tacosImg,
    'tacos-plate-steak': tacosImg,
    'tacos-plate-birria': tacosImg,
    'tacos-plate-shrimp': tacosImg,
    'single-taco-chicken-beef': tacosImg,
    'single-taco-steak-others': tacosImg,
    'single-taco-shrimp': tacosImg,
    'kids-chicken-tenders': kidsMealImg,
    'kids-cheese-quesadilla': kidsMealImg,
    'kids-taco': kidsMealImg,
    'kids-cheeseburger': kidsMealImg,
    'kids-acp': riceBeansImg,
    'fajitas-chicken': fajitasImg,
    'fajitas-steak': fajitasImg,
    'fajitas-shrimp': fajitasImg,
    'fajitas-combo': fajitasImg,
    'parrillada': fajitasImg,
    'cocktail-de-camaron': shrimpImg,
    'camarones-al-ajillo': shrimpImg,
    'camarones-a-la-diabla': shrimpImg,
    'camarones-empanizados': shrimpImg,
    'salmon-santa-fe': salmonImg,
    'arroz-chino-camarones': riceBeansImg,
    'arroz-chino-pollo': riceBeansImg,
    'acp-chicken': riceBeansImg,
    'acp-steak': riceBeansImg,
    'chimichanga-junniors': burritoImg,
    'side-rice': riceBeansImg,
    'side-beans': riceBeansImg,
    'side-grilled-chicken': grilledChickenImg,
    'side-steak': steakImg,
    'side-cheese-dip': cheeseDipImg,
    'side-guac-salad': guacamoleImg,
    'side-sour-cream': guacamoleImg,
    'side-avocado': guacamoleImg,
    'side-fries': kidsMealImg,
    'side-vegetables': tacoSaladImg,
    'side-tortillas': tacosImg,
    'side-shrimp': shrimpImg,
    'side-jalapenos-toreados': tacosImg,
    'side-grilled-onion': tacosImg,
    'side-lettuce': tacoSaladImg,
    'lunch-quesadilla': quesadillaImg,
    'lunch-burrito': burritoImg,
    'lunch-enchilada-taco': enchiladasImg,
    'lunch-3-tacos': tacosImg,
    'lunch-beans-cheese-nachos': nachosImg,
    'lunch-fajita-chicken': fajitasImg,
    'lunch-2-burritos': burritoImg,
    'lunch-combos': tacosImg,
    'chicken-soup': soupImg,
    'marucha-birria': soupImg,
    'seafood-soup': soupImg,
    'menudo': soupImg,
    'churros': churrosImg,
    'sopapillas': churrosImg,
    'fried-ice-cream': churrosImg,
    'flan': flanImg,
    'chocochimichanga': churrosImg,
    'mojito': drinksImg,
    'sunrise': drinksImg,
    'pina-colada': drinksImg,
    'strawberry-lemonade': drinksImg,
    'mango-paradise': drinksImg,
    'coke-products': drinksImg,
    'aguas-frescas': drinksImg,
    'jarritos': drinksImg,
  };

  // Prepare image metadata for SEO
  const menuImages: ImageMetadata[] = [
    { url: '/menu-hero.jpg', alt: 'Authentic Mexican food menu at Junnior\'s Tacos in Carrollton GA', width: 1200, height: 630 },
    { url: tacosImg, alt: 'Fresh authentic Mexican tacos - Junnior\'s Tacos Carrollton GA' },
    { url: burritoImg, alt: 'Delicious Mexican burritos - authentic recipes Carrollton GA' },
    { url: fajitasImg, alt: 'Sizzling fajitas - Mexican restaurant specialties Carrollton GA' },
    { url: enchiladasImg, alt: 'Homemade enchiladas - traditional Mexican cuisine Carrollton' },
  ];

  return (
    <main className="bg-background">
      <SEO
        title="Menu - Junnior's Tacos Mexican Restaurant | Authentic Mexican Food Carrollton GA"
        description="Explore our authentic Mexican menu featuring tacos, burritos, fajitas, enchiladas, and more. Homemade recipes from family traditions. Order now in Carrollton, GA."
        keywords={['mexican food menu', 'authentic tacos carrollton', 'mexican restaurant menu', 'burritos carrollton ga', 'fajitas', 'enchiladas', 'mexican cuisine']}
        images={menuImages}
      />
      <StructuredData images={menuImages} pageType="menu" />
      
      <Tabs defaultValue="appetizers" className="w-full">
        {/* Secondary Menu Navigation */}
        <nav className="sticky top-20 z-40 shadow-md border-b border-border" style={{ backgroundColor: 'hsl(40, 40%, 90%)' }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-1 h-16">
              {/* Left Arrow Button - Always visible on mobile for better UX */}
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 bg-background hover:bg-muted rounded-full h-8 w-8 lg:h-10 lg:w-10 border border-border"
                onClick={() => scroll('left')}
                disabled={!showLeftArrow}
                style={{ opacity: showLeftArrow ? 1 : 0.3 }}
              >
                <ChevronLeft className="h-4 w-4 lg:h-6 lg:w-6" />
              </Button>

              {/* Categories Navigation */}
              <div 
                ref={scrollContainerRef}
                className="flex-1 overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ 
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <TabsList className="w-full h-auto rounded-none bg-transparent p-0 border-0 inline-flex justify-start min-w-max">
                  {displayCategories.map((cat, index) => (
                    <div key={cat.value} className="flex items-center">
                      <TabsTrigger
                        value={cat.value}
                        onClick={scrollToTop}
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-bold data-[state=active]:bg-transparent bg-transparent px-3 py-4 lg:px-6 font-normal uppercase text-xs lg:text-sm tracking-wide whitespace-nowrap hover:bg-muted/50 transition-colors"
                      >
                        {cat.label}
                      </TabsTrigger>
                      {index < displayCategories.length - 1 && (
                        <div className="h-4 lg:h-6 w-px bg-border" />
                      )}
                    </div>
                  ))}
                </TabsList>
              </div>

              {/* Right Arrow Button - Always visible on mobile for better UX */}
              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 bg-background hover:bg-muted rounded-full h-8 w-8 lg:h-10 lg:w-10 border border-border"
                onClick={() => scroll('right')}
                disabled={!showRightArrow}
                style={{ opacity: showRightArrow ? 1 : 0.3 }}
              >
                <ChevronRight className="h-4 w-4 lg:h-6 lg:w-6" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Menu Content */}
        <div className="container mx-auto max-w-7xl py-8 px-4">
          {displayCategories.map((cat) => {
            const items = Object.entries(menuItems)
              .filter(([category]) => cat.includes.includes(category))
              .flatMap(([, items]) => items);
            
            return (
              <TabsContent key={cat.value} value={cat.value} className="mt-0">
                <h2 className="text-3xl font-bold text-center mb-8 uppercase tracking-wide">
                  {cat.label}
                </h2>
                {items.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {items.map((item) => (
                      <MenuItem
                        key={item.id}
                        name={item.name}
                        price={item.price}
                        description={item.description || undefined}
                        image={imageMap[item.image_url || ''] || item.image_url || chipsImage}
                        phoneNumber={contactInfo.phone}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">{t('noItemsInCategory')}</p>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </div>
      </Tabs>
    </main>
  );
};

export default Menu;
